import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { milestoneApi } from '../../api/client';
import type { MilestoneSummary } from '../../types';
import { format } from 'date-fns';
import { parseEventDate } from '../../utils/eventDate';

export function MilestonesListPage() {
  const [milestones, setMilestones] = useState<MilestoneSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareUrls, setShareUrls] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  function load() {
    setLoading(true);
    milestoneApi.list().then(setMilestones).finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this milestone? Any still-pending contributions will be discarded — already-approved photos stay in your archive.')) return;
    setBusyId(id);
    await milestoneApi.remove(id);
    setMilestones(prev => prev.filter(m => m.id !== id));
    setBusyId(null);
  }

  async function handleShare(id: string) {
    setBusyId(id);
    const { publicUrl } = await milestoneApi.publish(id);
    setShareUrls(prev => ({ ...prev, [id]: publicUrl }));
    setMilestones(prev => prev.map(m => m.id === id ? { ...m, isPublic: true } : m));
    setBusyId(null);
  }

  async function handleUnshare(id: string) {
    setBusyId(id);
    await milestoneApi.unpublish(id);
    setShareUrls(prev => { const next = { ...prev }; delete next[id]; return next; });
    setMilestones(prev => prev.map(m => m.id === id ? { ...m, isPublic: false } : m));
    setBusyId(null);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-xl">✨</span>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">My Milestones</h1>
        </div>
        <Link
          to="/events/milestones/new"
          className="px-3 lg:px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold shadow-md shadow-indigo-200 transition-colors"
        >
          + New Milestone
        </Link>
      </div>
      <p className="text-slate-500 text-sm mb-6">Chronological stories built from photos already in your archive.</p>

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : milestones.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-indigo-200">
          <p className="text-slate-500 font-medium">No milestones yet</p>
          <p className="text-slate-400 text-sm mt-1">Build a chronological story for someone in your family</p>
          <Link to="/events/milestones/new" className="inline-block mt-5 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-200">
            ✨ Create a Milestone Slideshow
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {milestones.map(m => (
            <div key={m.id} data-testid="milestone-card" className="bg-white rounded-2xl border border-indigo-50 shadow-sm overflow-hidden flex flex-col">
              <div className="h-32 bg-indigo-50 flex items-center justify-center overflow-hidden">
                {m.thumbnail ? (
                  <img src={m.thumbnail.filePath} alt="" loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl">✨</span>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h2 className="font-serif font-bold text-slate-900 mb-0.5 truncate">{m.title}</h2>
                <p className="text-xs text-slate-500 mb-2">
                  {m.person?.name ?? 'Unknown'}
                  {m.startDate && ` · ${format(parseEventDate(m.startDate), 'MMM yyyy')}`}
                  {m.endDate && ` – ${format(parseEventDate(m.endDate), 'MMM yyyy')}`}
                </p>
                <p className="text-sm text-slate-600 mb-3">{m.totalPhotos} photo{m.totalPhotos === 1 ? '' : 's'}</p>

                {m.pendingContributionCount > 0 && (
                  <Link
                    to={`/events/milestones/${m.id}/review`}
                    className="inline-block mb-3 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold self-start hover:bg-amber-200 transition-colors"
                  >
                    {m.pendingContributionCount} new to review
                  </Link>
                )}

                {shareUrls[m.id] && (
                  <div className="mb-3 flex items-center gap-1.5">
                    <input readOnly value={shareUrls[m.id]} className="flex-1 min-w-0 text-xs border border-emerald-200 rounded-lg px-2 py-1 bg-emerald-50" />
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(shareUrls[m.id])}
                      className="px-2 py-1 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 flex-shrink-0"
                    >
                      Copy
                    </button>
                  </div>
                )}

                <div className="mt-auto flex items-center gap-1.5 flex-wrap text-xs">
                  <Link
                    to={`/carousel?mode=milestone&milestoneId=${m.id}`}
                    className="px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                  >
                    ▶ Play
                  </Link>
                  <Link
                    to={`/events/milestones/${m.id}/edit`}
                    className="px-2.5 py-1.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
                  >
                    ✏️ Edit
                  </Link>
                  {m.isPublic ? (
                    <button
                      type="button"
                      disabled={busyId === m.id}
                      onClick={() => handleUnshare(m.id)}
                      className="px-2.5 py-1.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors disabled:opacity-50"
                    >
                      🔗 Unshare
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={busyId === m.id}
                      onClick={() => handleShare(m.id)}
                      className="px-2.5 py-1.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors disabled:opacity-50"
                    >
                      🔗 Share
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busyId === m.id}
                    onClick={() => handleDelete(m.id)}
                    className="px-2.5 py-1.5 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
